import type { ButtonNode, MenuNode, MenuSchema, TextNode } from '../MenuSchema.js';

export const SETTINGS_MENU_ACTIONS = {
    toggleRotationMode: 'settings-toggle-rotation-mode',
    adjustRotationSpeed: 'settings-adjust-rotation-speed',
    toggleEchoCancellation: 'settings-toggle-echo-cancellation',
    toggleNoiseSuppression: 'settings-toggle-noise-suppression',
} as const;

export const SETTINGS_MENU_LABELS = {
    title: 'Settings',
    rotationSection: 'Rotation (VR Mode)',
    rotationModeSmooth: 'Rotation: Smooth',
    rotationModeSnap: 'Rotation: Snap',
    smoothSpeedPrefix: 'Smooth Speed:',
    snapAnglePrefix: 'Snap Angle:',
    audioSection: 'Audio',
    echoOn: 'Echo Cancel: On',
    echoOff: 'Echo Cancel: Off',
    noiseOn: 'Noise Suppress: On',
    noiseOff: 'Noise Suppress: Off',
    helperText: 'Changes take effect immediately or on re-enter.',
    webAudioCardTitle: 'Audio Settings',
    webRotationCardTitle: 'Rotation Settings (VR Mode)',
    webDebugTitle: 'Persisted User Settings (Debug)',
    webRotationModeLabel: 'Rotation Mode',
    webSmoothLabel: 'Smooth Rotation Speed (deg/s)',
    webSnapLabel: 'Snap Rotation Angle (degrees)',
    webEchoLabel: 'Echo Cancellation',
    webNoiseLabel: 'Noise Suppression',
    webDebugCopy: 'Copy',
    webDebugRefresh: 'Refresh',
} as const;

export function createSettingsMenuSchema(): MenuSchema {
    return {
        id: 'SETTINGS',
        title: SETTINGS_MENU_LABELS.title,
        nodes: [
            { type: 'text', text: SETTINGS_MENU_LABELS.rotationSection, platforms: ['vr']},
            { type: 'button', id: SETTINGS_MENU_ACTIONS.toggleRotationMode, platforms: ['vr'], label: SETTINGS_MENU_LABELS.rotationModeSmooth },
            { type: 'text', text: () => `${SETTINGS_MENU_LABELS.smoothSpeedPrefix} 90°/s`, platforms: ['vr'] },
            { type: 'slider', id: SETTINGS_MENU_ACTIONS.adjustRotationSpeed, platforms: ['vr'], min: 10, max: 360, step: 5, get: () => 90, label: () => `${SETTINGS_MENU_LABELS.smoothSpeedPrefix} 90°/s` },
            { type: 'text', text: SETTINGS_MENU_LABELS.audioSection },
            { type: 'button', id: SETTINGS_MENU_ACTIONS.toggleEchoCancellation, label: SETTINGS_MENU_LABELS.echoOff },
            { type: 'button', id: SETTINGS_MENU_ACTIONS.toggleNoiseSuppression, label: SETTINGS_MENU_LABELS.noiseOff },
            { type: 'text', text: SETTINGS_MENU_LABELS.helperText },
        ],
    };
}

function findSchemaNodeById(schema: MenuSchema, id: string): MenuNode | undefined {
    return schema.nodes.find((n) => (n as { id?: string }).id === id);
}

export function readSettingsSchemaButtonLabel(schema: MenuSchema, id: string, fallback: string): string {
    const node = findSchemaNodeById(schema, id) as ButtonNode | undefined;
    if (!node || node.type !== 'button') {
        return fallback;
    }
    return typeof node.label === 'function' ? node.label() : node.label;
}

export function readSettingsSchemaTextAt(schema: MenuSchema, index: number, fallback: string): string {
    const textNodes = schema.nodes.filter((n): n is TextNode => n.type === 'text');
    const node = textNodes[index];
    if (!node) {
        return fallback;
    }
    return typeof node.text === 'function' ? node.text() : node.text;
}

export function formatRotationModeLabel(mode: 'smooth' | 'snap'): string {
    return mode === 'smooth' ? SETTINGS_MENU_LABELS.rotationModeSmooth : SETTINGS_MENU_LABELS.rotationModeSnap;
}

export function formatRotationSpeedLabel(mode: 'smooth' | 'snap', value: number): string {
    return mode === 'smooth'
        ? `${SETTINGS_MENU_LABELS.smoothSpeedPrefix} ${Math.round(value)}°/s`
        : `${SETTINGS_MENU_LABELS.snapAnglePrefix} ${Math.round(value)}°`;
}

export function formatEchoLabel(enabled: boolean): string {
    return enabled ? SETTINGS_MENU_LABELS.echoOn : SETTINGS_MENU_LABELS.echoOff;
}

export function formatNoiseLabel(enabled: boolean): string {
    return enabled ? SETTINGS_MENU_LABELS.noiseOn : SETTINGS_MENU_LABELS.noiseOff;
}
